import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Edit2, Check, X, Calendar } from 'lucide-react';
import SearchableUserDropdown from './SearchableUserDropdown';
import { User } from '../../services/user';

interface EditableCellProps {
  value: string | number | null;
  workshopId: number;
  fieldName: string;
  fieldType?: 'text' | 'number' | 'date' | 'select' | 'textarea' | 'user-select' | 'user-multi-select';
  selectOptions?: Array<{ value: string; label: string }>;
  users?: User[];
  onSave: (workshopId: number, fieldName: string, value: any) => Promise<void>;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  displayFormatter?: (value: any) => string;
  renderDisplayValue?: (value: any) => React.ReactNode;
}

// A table can provide the backend's object-level edit decision once for the
// whole row.  Existing callers that do not provide a context remain editable
// as before; Workshops supplies it for view-only additional-plant rows.
export const WorkshopEditabilityContext = React.createContext(true);

export const WorkshopEditabilityProvider: React.FC<{
  canEdit: boolean;
  children: React.ReactNode;
}> = ({ canEdit, children }) => (
  <WorkshopEditabilityContext.Provider value={canEdit}>
    {children}
  </WorkshopEditabilityContext.Provider>
);

const EditableCell: React.FC<EditableCellProps> = ({
  value,
  workshopId,
  fieldName,
  fieldType = 'text',
  selectOptions = [],
  users = [],
  onSave,
  disabled = false,
  placeholder = '',
  className = '',
  displayFormatter,
  renderDisplayValue
}) => {
  const inheritedCanEdit = React.useContext(WorkshopEditabilityContext);
  const isDisabled = disabled || !inheritedCanEdit;
  // Initialize edit value based on field type
  const getInitialEditValue = useCallback((): string | number | number[] | null => {
    if (fieldType === 'user-select') {
      // For user-select, we need the actual user ID, not the display name
      // Try to find the user ID from the display value or use the original value
      if (typeof value === 'number') return value;
      if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value);
      // If it's a name, try to find the user ID
      const user = users.find(u => `${u.first_name} ${u.last_name}`.trim() === value || u.username === value);
      return user ? user.id : null;
    } else if (fieldType === 'user-multi-select') {
      // For multi-select, return empty array initially - will be populated by SearchableUserDropdown
      return [];
    }
    return value || '';
  }, [fieldType, value, users]);

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState<string | number | number[] | null>(() => getInitialEditValue());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);

  // Format display value
  const displayValue = displayFormatter ? displayFormatter(value) : (value?.toString() || '');

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement || inputRef.current instanceof HTMLTextAreaElement) {
        inputRef.current.select();
      }
    }
  }, [isEditing]);

  // Reset edit value when value changes, but only if not currently editing
  useEffect(() => {
    if (!isEditing) {
      setEditValue(getInitialEditValue());
    }
  }, [value, users, fieldType, isEditing]);

  const handleEdit = () => {
    if (!isDisabled) {
      setEditValue(getInitialEditValue());
      setIsEditing(true);
      setError(null);
    }
  };

  const handleSave = async () => {
    // Don't save if no change
    if (editValue === value) {
      setIsEditing(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Convert value based on field type
      let processedValue = editValue;
      if (fieldType === 'number' && editValue !== '' && editValue !== null) {
        processedValue = parseFloat(editValue.toString());
        if (isNaN(processedValue)) {
          throw new Error('Invalid number format');
        }
      } else if (fieldType === 'user-select') {
        // For single user selection, save the user ID
        processedValue = editValue ? Number(editValue) : null;
      } else if (fieldType === 'user-multi-select') {
        // For multiple user selection, save array of user IDs
        processedValue = Array.isArray(editValue) ? editValue : [];
      }

      await onSave(workshopId, fieldName, processedValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save cell value:', error);
      setError('Failed to save changes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEditValue(getInitialEditValue());
    setIsEditing(false);
    setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && fieldType !== 'textarea') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleDoubleClick = () => {
    handleEdit();
  };

  if (isEditing) {
    return (
      <div className={`relative ${className}`}>
        <div className="flex items-center space-x-1">
          {fieldType === 'select' ? (
            <select
              ref={inputRef as React.RefObject<HTMLSelectElement>}
              value={Array.isArray(editValue) ? editValue.join(', ') : (editValue?.toString() || '')}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleSave}
              disabled={isLoading}
              className="text-sm bg-white border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-0 flex-1"
            >
              <option value="">{placeholder || 'Select...'}</option>
              {selectOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : fieldType === 'user-select' ? (
            <div className="flex-1">
              <SearchableUserDropdown
                users={users}
                selectedUsers={editValue && typeof editValue === 'number' ? [editValue] : []}
                onSelectionChange={(selectedUsers) => {
                  const userId = selectedUsers.length > 0 ? selectedUsers[0] : null;
                  setEditValue(userId);
                }}
                multiple={false}
                placeholder={placeholder || 'Search and select mentor...'}
                className="text-sm"
              />
            </div>
          ) : fieldType === 'user-multi-select' ? (
            <div className="flex-1">
              <SearchableUserDropdown
                users={users}
                selectedUsers={Array.isArray(editValue) ? editValue : []}
                onSelectionChange={(selectedUsers) => {
                  setEditValue(selectedUsers);
                }}
                multiple={true}
                placeholder={placeholder || 'Search and select mentees...'}
                className="text-sm"
              />
            </div>
          ) : fieldType === 'textarea' ? (
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              value={Array.isArray(editValue) ? editValue.join(', ') : (editValue?.toString() || '')}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleSave}
              disabled={isLoading}
              placeholder={placeholder}
              rows={2}
              className="text-sm bg-white border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-0 flex-1 resize-none"
            />
          ) : (
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              type={fieldType === 'date' ? 'date' : fieldType === 'number' ? 'number' : 'text'}
              value={Array.isArray(editValue) ? editValue.join(', ') : (editValue?.toString() || '')}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleSave}
              disabled={isLoading}
              placeholder={placeholder}
              className="text-sm bg-white border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-0 flex-1"
            />
          )}
          
          {(fieldType === 'user-select' || fieldType === 'user-multi-select') ? (
            <>
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="p-1 text-green-600 hover:text-green-800 disabled:opacity-50"
                title="Save"
              >
                <Check className="h-3 w-3" />
              </button>
              <button
                onClick={handleCancel}
                disabled={isLoading}
                className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                title="Cancel"
              >
                <X className="h-3 w-3" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="p-1 text-green-600 hover:text-green-800 disabled:opacity-50"
                title="Save"
              >
                <Check className="h-3 w-3" />
              </button>
              <button
                onClick={handleCancel}
                disabled={isLoading}
                className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                title="Cancel"
              >
                <X className="h-3 w-3" />
              </button>
            </>
          )}
        </div>
        {error && (
          <div className="absolute top-full left-0 mt-1 text-xs text-red-600 bg-white border border-red-200 rounded px-2 py-1 shadow-sm z-10">
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div 
      className={`group relative cursor-pointer ${className}`}
      onDoubleClick={handleDoubleClick}
      title={`Double-click to edit${isDisabled ? ' (disabled)' : ''}`}
    >
      <div className="flex items-center justify-between">
        <span className={`${!displayValue && 'text-gray-400 italic'}`}>
          {renderDisplayValue ? renderDisplayValue(value) : (displayValue || placeholder || 'Click to edit')}
        </span>
        
        {!isDisabled && (
          <button
            onClick={handleEdit}
            disabled={isLoading}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            title="Edit"
          >
            <Edit2 className="h-3 w-3" />
          </button>
        )}
      </div>
      
      {error && (
        <div className="absolute top-full left-0 mt-1 text-xs text-red-600 bg-white border border-red-200 rounded px-2 py-1 shadow-sm z-10">
          {error}
        </div>
      )}
    </div>
  );
};

export default EditableCell;



